//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * @class am.RoofLayerController
     *
     * @fires RoofLayerController#new_feature_selection
     * @fires RoofLayerController#feature_added_to_selection
     * @fires RoofLayerController#feature_removed_from_selection
     * @fires RoofLayerController#feature_selection_cleared
     * @listens event:select_feature_at_location
     * @listens event:clear_feature_selection
     *
     * This class controls the layer containing the roofside geometries.
     */
    module.RoofLayerController = function() {
        // Private attributes
        this._vectorLayer = undefined; // reference to the Leaflet layer containing the geometry of the roofs / buildings
        this._rasterLayer = undefined; // reference to the Leaflet layer containing the irradiation of each roof pixel
        this._rasterParkingsLayer = undefined;
        this._map  = undefined;        // reference to the map
        this._selectedRoofsideIds = new Set();
        this._selectedBuildingId = undefined;
        this._controlButton = undefined;
        this._config = {
            vectorTileFolder: undefined,
            vectorTileOptions: {
                rendererFactory: L.canvas.tile,
                attribution: 'Potentiel solaire &copy; <a href="http://www.cythelia.fr/">Cythelia</a>'
            },
            vectorStrokeMinZoom: 18,
            rasterTileFolder: undefined,
            rasterTileOptions: {
                tms: true,
                opacity: 0.5
            },
            multipleSelection: false,
            roofIdField: "UID",
            buildingIdField: "ID_batimnt",
            irradiationField: "moduleIrra",
            addControlButton: true,
            colorControlButtonLegend: true,
            addExtraLegendEntry: false
        };
        this._mainController = null;
    };

    /**
     * Class constructor
     * @constructor
     *
     * @param {Object} mainController - Reference to the main controller of the application
     * @param {Object} config - Configuration to be applied to the controller. All the options which can be passed to 
     *      the constructor of class am.view.SelectableLayer are also accepted here, in addition to the following 
     *      parameters:
     *          - "vectorTileFolder": Path to the folder containing the layer tiles (in PBF format). The folder must be 
     *            specified using Unix style (ex: 'path/to/folder') and not Windows style (ex: 'path\\to\\folder'). The 
     *            layer tiles MUST have been created with tippecanoe (see https://github.com/mapbox/tippecanoe).
     *            THIS PARAMETER IS MANDATORY.
     *          - "roofIdField": Name of the layer field which contains the IDs of the roof sides (defaults to "UID")
     *          - "buildingIdField": Name of the layer field which contains the IDs of the buildings (defaults to 
     *              "ID_batimnt")
     */
    module.RoofLayerController.prototype.init = function(mainController, config) {
        // Instantiate the private attributes
        this._mainController = mainController;
        
        if (!config.multipleSelection) { // if no multiple selection is allowed, set buildingIdField and roofIdField to the same value
            config.roofIdField = config.roofIdField || config.buildingIdField;
            config.buildingIdField = config.buildingIdField || config.roofIdField;
        }
        this._config = am.utils.mergeObjects(this._config, config);
        var appConfig = mainController.getConfig();
        if (appConfig.hasOwnProperty('firstParkingFeatureId') && appConfig.firstParkingFeatureId >= 0) {
            this._config.firstParkingFeatureId = appConfig.firstParkingFeatureId;
        }
        this._map = mainController.getMapObject();
        if (this._config.rasterTileFolder) {
            this._rasterLayer = this._createRasterLayer(this._config.rasterTileFolder, this._config.rasterTileOptions);
            this._rasterLayer.addTo(this._map);
            $(this._rasterLayer.getContainer()).addClass('am-roof-layer-raster');
        }
        if (this._config.rasterParkingsTileFolder) {
            this._rasterParkingsLayer = this._createRasterLayer(this._config.rasterParkingsTileFolder, this._config.rasterTileOptions);
            this._rasterParkingsLayer.addTo(this._map);
            $(this._rasterParkingsLayer.getContainer()).addClass('am-roof-layer-raster');
        }
        if (this._config.hasOwnProperty('additionalRasterLayers') && this._config.additionalRasterLayers.length > 0) {
            this._config.additionalRasterLayers.forEach(function(layer) {
                var options = am.utils.mergeObjects(this._config.rasterTileOptions, layer.options);
                var rasterLayer = this._createRasterLayer(layer.folder, options);
                rasterLayer.addTo(this._map);
                $(rasterLayer.getContainer()).addClass('am-roof-layer-raster');
            }.bind(this));
        }
        this._vectorLayer = this._createVectorLayer(appConfig).addTo(this._map);
        $(this._vectorLayer.getContainer()).addClass('am-roof-layer-vector');
        if (this._config.addControlButton) {
            this._controlButton = this._createControlButton(appConfig).addTo(this._map);
        }

        // Register event handlers associated with the panel
        am.eventManager.register('select_features_by_id', this._selectFeaturesById, this);
        if (!this._config.hasOwnProperty('disableSelectionAtLocation') || !this._config.disableSelectionAtLocation) {
            am.eventManager.register('select_feature_at_location', this._selectFeatureAtLocation, this);
            this._vectorLayer.on('click tap', this._handleLayerClick, this);
        }
        am.eventManager.register('clear_feature_selection', this._unselectAllFeatures, this);
        am.eventManager.register('hide_roof_layer', this.hideRoofLayerField, this);
        am.eventManager.register('show_roof_layer', this.showRoofLayerField, this);
        this._map.on('click tap', this._handleMapClick, this);
    };

    /**
     * Class destructor
     */
    module.RoofLayerController.prototype.destroy = function() {
        // Remove event handlers associated with the panel
        am.eventManager.unregister('select_features_by_id', this._selectFeaturesById, this);
        if (!this._config.hasOwnProperty('disableSelectionAtLocation') || !this._config.disableSelectionAtLocation) {
            am.eventManager.unregister('select_feature_at_location', this._selectFeatureAtLocation, this);
            this._vectorLayer.off('click tap', this._handleLayerClick, this);
        }
        am.eventManager.unregister('clear_feature_selection', this._unselectAllFeatures, this);
        am.eventManager.unregister('hide_control_button', this.hideRoofLayerField, this);
        am.eventManager.unregister('show_control_button', this.hideRoofLayerField, this);
        this._map.off('click tap', this._handleMapClick, this);

        // Remove panel from the map
        this._vectorLayer.remove();
        if (this._rasterLayer) {
            this._rasterLayer.remove();
        }
        if(this._rasterParkingsLayer){
            this._rasterParkingsLayer.remove();
        }
        if (this._controlButton) {
            this._controlButton.remove();
        }
        this._map = undefined;
    }    

    /**
     * Instantiates the roof layer
     *
     * @return {am.SelectableLayerView} - New instance of class am.SelectableLayerView corresponding to the view 
     *      associated with the present controller
     */
    module.RoofLayerController.prototype._createVectorLayer = function(config) {
        // Check input folder
        var tileFolder = this._config.vectorTileFolder;
        if (tileFolder.charAt(tileFolder.length - 1) != '/') {
            tileFolder += '/';
        }

        // Define default styles of the selected, non-selected and disabled features
        var layerOptions = this._config.vectorTileOptions;
        if (config.irradiation.year && layerOptions.attribution) {
            layerOptions.attribution += ' ' + config.irradiation.year;
        }
        var self = this;
        if (!this._config.rasterTileFolder) { // there is no raster layer: color the interior of the polygons
            // Default drawing options
            var drawOptions = {
                stroke: true,
                weight: 2,         // stroke width
                opacity: 0.9,      // stroke alpha
                lineJoin: 'round',
                dashArray: null,
                dashOffset: null,
                fill: true,
                fillOpacity: 0.7   // fill alpha
            };

            // Define the colors to be used to color the polygons
            var colors = [];
            colors.push({ "irradiation": 0, "value": '#50689b' }); // buildings with negative irradiation value will be displayed in blue
            config.irradiation.classes.forEach(function(irradClass) {
                var value = irradClass.upperLimit;
                if (irradClass.units == "%") {
                    value *= config.irradiation.maxValue / 100;
                }
                colors.push({ "irradiation": value, "value": irradClass.upperLimitColor });
            });
            colors[colors.length - 1].irradiation = Infinity;

            // Define the function used to color the polygons
            var irradiationField = this._config.irradiationField;
            if (!irradiationField) {
                throw new Error("Please specify the field of the vector layer which contains the values of irradiation");
            }
            var createColorFunction = function(featureStyleBase) {
                return function (featureProperties, zoomLevel) {
                    var irradiation = featureProperties[irradiationField];
                    for (var iColor = 0, nColors = colors.length; iColor < nColors; ++iColor) {
                        if (irradiation < colors[iColor].irradiation) {
                            var color = colors[iColor].value;
                            var featureStyle = am.utils.shallowCopy(featureStyleBase);
                            featureStyle.color = color;
                            featureStyle.fillColor = color;
                            if (zoomLevel < self._config.vectorStrokeMinZoom) {
                                featureStyle.stroke = false;
                            }
                            return featureStyle;
                        }
                    }
                };
            };

            // Define the layer styles
            layerOptions.defaultFeatureStyle = layerOptions.defaultFeatureStyle || createColorFunction(drawOptions);
            layerOptions.selectedFeatureStyle = layerOptions.selectedFeatureStyle || createColorFunction(
                am.utils.mergeObjects(drawOptions, {
                    weight: 4,
                    opacity: 0.8,
                    color: 'black'
                })
            );
            layerOptions.disabledFeatureStyle = layerOptions.disabledFeatureStyle || {
                stroke: false,
                fill: true,
                fillColor: 'black',
                fillOpacity: 0.6
            };
        } else { // a raster layer is present: do not fill the polygons
            var drawOptions = {
                stroke: true,
                weight: 2,    // stroke width
                opacity: 0.7, // stroke alpha
                color: 'rgb(102, 51, 0)', // stroke color
                lineJoin: 'round',
                dashArray: null,
                dashOffset: null,
                fill: false
            };
            var createColorFunction = function(featureStyleBase) {
                return function (featureProperties, zoomLevel) {
                    var featureStyle = am.utils.shallowCopy(featureStyleBase);
                    if (zoomLevel < self._config.vectorStrokeMinZoom) {
                        featureStyle.stroke = false;
                    }

                    var featureId = featureProperties[self._config.roofIdField];
                    if (featureId === -9) {
                        featureStyle.fill = true;
                        featureStyle.fillColor = '#a6a6a6';
                        featureStyle.fillOpacity = 1.0;
                    }
                    // #1198 : Les parkings ne se grisent pas avec cette condition.
                    /*
                    else if (self._config.hasOwnProperty("firstParkingFeatureId") && featureId >= self._config.firstParkingFeatureId) {
                        if (!self._mainController._fromPrivateApp()) {
                            featureStyle.stroke = false;
                            featureStyle.color = '#4759c7';
                            featureStyle.fill = false;
                            featureStyle.fillColor = '#8091a6';
                            featureStyle.fillOpacity = 0;
                        }
                        else {
                            featureStyle.color = '#4759c7';
                            featureStyle.fill = true;
                            featureStyle.fillColor = '#8091a6';
                            featureStyle.fillOpacity = 0.75;
                        }
                    }
                    //*/
                    return featureStyle;
                };
            };
            layerOptions.defaultFeatureStyle = createColorFunction(layerOptions.defaultFeatureStyle || drawOptions);
            layerOptions.selectedFeatureStyle = createColorFunction(layerOptions.selectedFeatureStyle ||
                am.utils.mergeObjects(drawOptions, {
                    weight: 4,
                    opacity: 0.8,
                    color: 'black'
                }));
            layerOptions.disabledFeatureStyle = createColorFunction(layerOptions.disabledFeatureStyle || {
                    stroke: false,
                    fill: true,
                    fillColor: 'black',
                    fillOpacity: 0.6
                });
        }

        // Create view
        var view;
        if (this._config.geographicBorders) {
            layerOptions.boundary = this._config.geographicBorders;
            view = am.utils.selectableLayerView.boundaryCanvas(tileFolder + '{z}/{x}/{y}.pbf', this._config.roofIdField, "roofs", layerOptions);
        } else {
            view = am.utils.selectableLayerView(tileFolder + '{z}/{x}/{y}.pbf', this._config.roofIdField, "roofs", layerOptions);
        }
        
        return view;
    };

    /**
     * Instantiates the layer displaying the irradiation of each roof pixel
     *
     * @return {L.TileLayer} - New instance of class L.TileLayer containing the roof irradiation
     */
    module.RoofLayerController.prototype._createRasterLayer = function(tileFolder, options) {
        var view = null;
        if (tileFolder.charAt(tileFolder.length - 1) !== '/') {
            tileFolder += '/';
        }
        if (this._mainController._config.hasOwnProperty('initialState') && this._mainController._config.initialState.epciJson) {
            view = L.TileLayer.boundaryCanvas(
                tileFolder + '{z}/{x}/{y}.png',
                Object.assign({
                    crossOrigin: true
                }, options, {
                    boundary: this._mainController._config["initialState"].epciJson,
                    attribution: "Archelios MAP",
                    trackAttribution: true
                })
            );
        } else if (this._config.geographicBorders) {
            options = am.utils.mergeObjects(options, {
                boundary: this._config.geographicBorders,
                trackAttribution: false
            });
            view = L.TileLayer.boundaryCanvas(tileFolder + '{z}/{x}/{y}.png', options);
        } else {
            view = L.tileLayer(tileFolder + '{z}/{x}/{y}.png', options);
        }
        return view;
    };

    /**
     * Instantiates the button which enables the user to hide or show the roof layer
     *
     * @return {L.Control} - Control button to be added to the map
     */
    module.RoofLayerController.prototype._createControlButton = function(config) {
        // Create the button
        var button = am.utils.twoStateButton({
            cssClass: "am-roof-layer-button am-widget-frame",
            activeStateContentsClass: 'am-hide-roofs-image',
            inactiveStateContentsClass: 'am-show-roofs-image',
            activeStateTooltip: am.translator.translate('hideRoofLayer'),
            inactiveStateTooltip: am.translator.translate('showRoofLayer')
        });

        // Create the legend below the button
        var irradClasses = am.utils.getIrradClasses(config);
        if (irradClasses.length > 1) {
            // Create the legend contents
            var $legend = $('<div class="am-roof-layer-legend-container"></div>');
            var $contents = $('<div class="am-roof-layer-legend am-widget-frame"></div>');
            $legend.append($contents);
            $contents.append('<div class="picto am-legend-left-end" data-toggle="tooltip" title="' +
                am.translator.translate('goodSolarPotential') + '"><i class="am-icon am-sun-icon am-sun-icon-bright am-icon-lgd"></i></div>');
            $contents.append('<div class="picto am-legend-right-end"  data-toggle="tooltip" title="' +
                am.translator.translate('badSolarPotential') + '"><i class="am-icon am-sun-icon am-sun-icon-dim am-icon-lgd"></i></div>');
            if (this._config.addExtraLegendEntry) {
                $legend.append('<div class="am-extra-roof-layer-legend-entry am-widget-frame"  data-toggle="tooltip" title="' +
                    am.translator.translate('noInstallationPossible') + '"><i class="am-icon am-forbidden-icon am-icon-btn"></i></div>');
            }

            // Color the legend's background
            if (this._config.colorControlButtonLegend) {
                var backgroundColor = 'linear-gradient(to top, ' + irradClasses[0].lowerLimitColor;
                var lowerRangeLimit = irradClasses[0].lowerLimit || ((irradClasses[0].hasOwnProperty('units') && irradClasses[0].units === '%') ? 50 : 0.5);
                var upperRangeLimit = irradClasses[irradClasses.length - 1].upperLimit;
                for (var iClass = 0, nClasses = irradClasses.length; iClass < nClasses; ++iClass) {
                    var currIrradClass = irradClasses[iClass];
                    var breakPoint = (currIrradClass.upperLimit - lowerRangeLimit) / (upperRangeLimit - lowerRangeLimit);
                    backgroundColor += ', ' + currIrradClass.upperLimitColor + ' ' + Math.round(breakPoint * 100) + '%';
                }
                backgroundColor += ')';
                $contents.css('background', backgroundColor);
            }

            // Add the legend below the button
            button.addHtmlContentsNextToButton($legend);
        }

        // Define the callbacks which will be executed when the user clicks on the button
        var _this = this;
        var activeStateCallback = function() {
            $(_this._vectorLayer.getContainer()).addClass('hidden');
            if (_this._config.rasterTileFolder) {
                $(_this._rasterLayer.getContainer()).addClass('hidden');
            }
            if (_this._config.rasterParkingsTileFolder) {
                $(_this._rasterParkingsLayer.getContainer()).addClass('hidden');
            }
            $('.am-roof-layer-legend-container').slideUp();
        };
        var inactiveStateCallback = function () {
            if (_this._config.rasterTileFolder) {
                $(_this._rasterLayer.getContainer()).removeClass('hidden');
            }
            if (_this._config.rasterParkingsTileFolder) {
                $(_this._rasterParkingsLayer.getContainer()).removeClass('hidden');
            }
            $(_this._vectorLayer.getContainer()).removeClass('hidden');
            $('.am-roof-layer-legend-container').slideDown();
        };
        button.setClickCallbacks(activeStateCallback, inactiveStateCallback);

        return button;
    };

    /**
     * Instantiates the legend
     *
     * @return {L.Control} - Control to be added to the map
     */
    module.RoofLayerController.prototype._createLegend = function(config) {
        // Define the contents of the legend widget
        var irradClasses = am.utils.getIrradClasses(config);
        var $contents = $('<div>').addClass('am-legend-widget-contents');
        if (irradClasses.length > 1) {
            var irradClassLeft = irradClasses[0];
            var $textLeft = $('<div>').text(am.translator.translate(irradClassLeft.titleStringCode));
            $textLeft.addClass('am-legend-contents-lower-end');
            if (irradClassLeft.textColor) {
                $textLeft.css('color', irradClassLeft.textColor);
            }

            var irradClassRight = irradClasses[irradClasses.length - 1];
            var $textRight = $('<div>').text(am.translator.translate(irradClassRight.titleStringCode));
            $textRight.addClass('am-legend-contents-upper-end');
            if (irradClassRight.textColor) {
                $textRight.css('color', irradClassRight.textColor);
            }

            $contents.append($textLeft);
            $contents.append($textRight);
        }

        // Color the contents of the legend widget
        if (irradClasses.length > 1) {
            var contentsColor = 'linear-gradient(to right';
            for (var iClass = 0, nClasses = irradClasses.length; iClass < nClasses; ++iClass) {
                var currIrradClass = irradClasses[iClass];
                var lowerLimit = (iClass == 0) ? 0 : irradClasses[iClass - 1].upperLimit;
                var upperLimit = currIrradClass.upperLimit;
                if (currIrradClass.units != "%") {
                    lowerLimit = Math.round(lowerLimit / config.irradiation.maxValue * 100);
                    upperLimit = Math.round(upperLimit / config.irradiation.maxValue * 100);
                }
                contentsColor += ', ';
                if (iClass == 0 || currIrradClass.lowerLimitColor != irradClasses[iClass - 1].upperLimitColor) {
                    contentsColor += currIrradClass.lowerLimitColor + ' ' + lowerLimit + '%,';
                }
                contentsColor += currIrradClass.upperLimitColor + ' ' + upperLimit + '%';
            }
            contentsColor += ')';
            $contents.css('background', contentsColor);
        }

        // Create the legend widget
        var legend = am.utils.legend({
            cssClass: "am-legend-widget",
            contents: $contents
        });

        return legend;
    };

    /**
     * Selects the feature(s) whose ID(s) is(are) passed as input argument(s). Note that this function will not 
     * only select the feature(s), but also highlight all the other features belonging to the same building.
     * @fires SelectableProtobuf#feature_selected
     *
     * @param {Object} options - Object having possibly the two following fields:
     *      - ids: [mandatory field]: IDs of the features to select
     *      - emitEvent: should an event be emitted when unselecting the previously selected features?
     */
    module.RoofLayerController.prototype._selectFeaturesById = function(options) {
        this._unselectAllFeatures(options.emitEvent);
        var _this = this;

        // On essaie tout d'abord de sélectionner les toitures en supposant qu'elles appartiennent à des tuiles
        // déjà chargées
        var waitForTilesToLoad = false;
        options.ids.forEach(function(id) {
            var success = _this._selectFeature(id);
            if (!success) {
                waitForTilesToLoad = true;
            }
        });

        // Si toutes les toitures n'ont pas pu être sélectionnées (car situées sur des tuiles pas encore chargées),
        // on teste chaque nouvelle tuile juste après son chargement. Lorsque toutes les toitures ont été sélectionnées,
        // on détache l'"event listener" de l'objet "layer".
        if (waitForTilesToLoad) {
            var featuresToFind = options.ids.slice(); // copy array
            this._vectorLayer.on('tileload', function callback() {
                if (featuresToFind.length > 0) {
                    var featureIds = featuresToFind.slice(); // copy array
                    featureIds.forEach(function(id) {
                        var success = _this._selectFeature(id);
                        if (success) {
                            featuresToFind.splice(featuresToFind.indexOf(id), 1);
                        }
                    });
                }
                if (featuresToFind.length == 0) {
                    _this._vectorLayer.off('tileload', callback); // detach self!    
                }
            });
        }
    };

    /**
     * Selects the roof side which is nearest to the point passed as input argument. Note that this function will not 
     * only select the roof side, but also highlight all the other roof sides belonging to the same building.
     * @fires SelectableProtobuf#feature_selected
     *
     * @param {L.LatLng} latLng - Point at which the roof side should be selected
     */
    module.RoofLayerController.prototype._selectFeatureAtLocation = function(latLng) {
        this._unselectAllFeatures();
        var featureId = this._vectorLayer.getFeatureClosestToPoint(latLng);
        this._selectFeature(featureId, { featureLocation: latLng });
    };

    /**
     * Unselects all the currently selected roof sides. Note that this function will also remove any highlighting which
     * had been previously set.
     * @fires SelectableProtobuf#feature_selection_cleared
     *
     * @param {Boolean} emitEvent - Should a 'feature_selection_cleared' event be fired? Defaults to true
     */
    module.RoofLayerController.prototype._unselectAllFeatures = function(emitEvent) {
        emitEvent = (emitEvent == undefined) ? true : emitEvent;
        this._selectedRoofsideIds.forEach(function(featureId) {
            this._unselectFeature(featureId, emitEvent);//#2414 mettre l'event en undefined car sinon on n'enleve pas les id des batiments dans info_panel_controller
        }, this);
    };

    /**
     * Selects (or unselects) the roof side on which the user clicked. Note that this function will not only select the 
     * roof side, but also highlight all the other roof sides belonging to the same building.
     * @fires SelectableProtobuf#feature_selected
     * @fires SelectableProtobuf#feature_unselected
     *
     * @param {Event} event - Event triggered by the roof layer after the user has clicked on a roof side
     */
    module.RoofLayerController.prototype._handleLayerClick = function(event) {
        L.DomEvent.stopPropagation(event);

        // Get the building ID of the clicked feature
        var clickedFeature   = event.layer;
        var clickedFeatureId = this._vectorLayer.options.getFeatureId(clickedFeature);
        var buildingId = clickedFeature.properties[this._config.buildingIdField];

        // If the building is in a zone in which the solar panels cannot be installed, display a popup window
            var irradiationValue = clickedFeature.properties[this._config.irradiationField];
        if ((irradiationValue !== undefined && irradiationValue < 0.) || clickedFeatureId == -9) {
                var maxWidth = Math.min($(window).width(), 400) - 60;
                var featureBounds = this._vectorLayer.getFeatureBounds(clickedFeatureId);
                var upperCenterPt = [featureBounds.getNorth(), featureBounds.getCenter().lng];
                
                var popup = L.popup({
                    closeOnClick: true,
                    className: "am-hidden am-forbidden-building-popup",
                    maxWidth: maxWidth
                });
            if (clickedFeatureId == -9) {
                popup.setContent(am.translator.translate('buildingInSevesoAreaPopupContents'));
                popup.setLatLng(featureBounds.getCenter());
            } else {
                popup.setContent((irradiationValue < -1.1) ?
                    am.translator.translate('buildingInZPPAUPPopupContents') :
                    am.translator.translate('historicalBuildingPopupContents'));
                popup.setLatLng(upperCenterPt);
            }
                
                var self = this;
                popup.on('add', function() {
                    self._unselectAllFeatures();

                    var $popupContainer = $(".am-forbidden-building-popup");
                    if ($popupContainer) {
                        am.eventManager.trigger('hide_address_search_field');
                        am.eventManager.trigger('hide_roof_layer');
                        am.eventManager.trigger('hide_base_layer_selector');

                        var height = $popupContainer.outerHeight();
                        self._map.fitBounds(featureBounds, {
                            paddingTopLeft: [0, height]
                        });
                        $popupContainer.removeClass('am-hidden');
                    }
                });
                popup.on('remove', function() {
                    am.eventManager.trigger('show_address_search_field');
                    am.eventManager.trigger('show_base_layer_selector');
                    am.eventManager.trigger('show_roof_layer');
                });
                
                popup.openOn(this._map);
                
                return;
        }

        // If the clicked roofside is not currently selected and belongs to the currently selected building, select it
        if (this._selectedRoofsideIds.size == 0 || 
                (!this._selectedRoofsideIds.has(clickedFeatureId) && this._selectedBuildingId == buildingId)) {
            this._selectFeature(clickedFeatureId, {
                featureLocation: event.latlng, 
                buildingId: buildingId
            });
        }
        // Else if the clicked roofside was already selected, unselect it
        else if (this._selectedRoofsideIds.has(clickedFeatureId)) {
            this._unselectFeature(clickedFeatureId);
        }
        // Else if a roofside was already selected and the user clicked on a roofside which is not belonging to the same
        // building, unselects the selected features and select the new roofside
        else {
            this._unselectAllFeatures();
            this._selectFeature(clickedFeatureId, {
                featureLocation: event.latlng, 
                buildingId: buildingId, 
                reinitializePanel: true
            });
        }
    };

    /**
     * Unselects the selected features if the user clicks on the map outside of a feature belonging to the 
     * current selection
     * @fires SelectableProtobuf#feature_selected
     * @fires SelectableProtobuf#feature_unselected
     *
     * @param {Event} event - Event triggered by the map after the user has clicked on the map outside of
     *      a feature belonging to the current selection
     */
    module.RoofLayerController.prototype._handleMapClick = function(event) {
        // Unselect all selected features only if the user clicked on the map (and not on the side panel)
        if ($(event.originalEvent.target).hasClass('leaflet-tile')) {
            this._unselectAllFeatures();
        }
    };

    /**
     * Selects the roof side whose ID is passed as input argument. Note that this function will not only select the 
     * roof side, but also highlight all the other roof sides belonging to the same building.
     * @fires SelectableProtobuf#new_feature_selection
     * @fires SelectableProtobuf#feature_added_to_selection
     *
     * @param {String|Number} featureId - ID of the roof side to select
     * @param {Object} options - Options, can be any of:
     *      - featureLocation {L.LatLng}: location of the feature to select
     *      - buildingId {String|Number}: ID of the building which contains the roof side to select
     *      - reinitializePanel {Boolean}: should the panel displaying information about the features be 
     *          reinitialized (i.e. start from its home page)? Defaults to true
     *      - sendEvent {Boolean}: should a 'new_feature_selection' or 'feature_added_to_selection' event be 
     *          emitted? Defaults to false
     * @return {Boolean} - True if the requested feature could be successfully selected, false otherwise
     */
    module.RoofLayerController.prototype._selectFeature = function(featureId, options) {
        // Select feature on the map and add its ID to the list of selected feature IDs
        this._selectedRoofsideIds.add(featureId);
        this._vectorLayer.selectFeature(featureId);

        // Get the feature location and building ID
        options = options || {};
        options.sendEvent = (options.sendEvent != undefined) ? options.sendEvent : true;
        options.reinitializePanel = (options.reinitializePanel != undefined) ? options.reinitializePanel : true;
        if (!options.featureLocation) {
            options.featureLocation = this._vectorLayer.getFeatureCentroid(featureId);
            if (!options.featureLocation) {
                return false;
            }
        }
        if (!options.buildingId) {
            var featureProperties = this._vectorLayer.getFeatureProperties(featureId);
            if (!featureProperties) {
                return false;
            }
            options.buildingId = featureProperties[this._config.buildingIdField];            
        }
        if (this._selectedBuildingId != undefined && this._selectedBuildingId != options.buildingId) {
            return false; // do nothing if the feature we are trying to select does not belong to the same building as the feature(s) already selected
        }

        // If no roofside was selected prior to the present one, get the ID of the building which contains the selected
        // feature, highlight its roofsides and get the extents of the building
        if (this._selectedBuildingId == undefined) { // no roofside is selected yet
            this._selectedBuildingId = options.buildingId;

            // Highlight all the roof sides belonging to the building
            var _this = this;
            var roofsideIds = this._vectorLayer.getFeatureIds(function(feature) {
                return feature.properties[_this._config.buildingIdField] == options.buildingId;
            });
            this._vectorLayer.highlightFeatures(roofsideIds);

            // Get the extents of the building
            var buildingBounds = undefined;
            roofsideIds.forEach(function(id) {
                var featureBounds = this.getFeatureBounds(id);
                if (buildingBounds == undefined) {
                    buildingBounds = featureBounds;
                } else {
                    buildingBounds.extend(featureBounds);
                }
            }, this._vectorLayer);

            // Send an event to notify the main controller that a new feature has been selected, if required
            if (options.sendEvent) {
                am.eventManager.trigger('new_feature_selection', {
                    roofsideId: featureId,
                    buildingId: options.buildingId,
                    pointLocation: options.featureLocation,
                    buildingBounds: buildingBounds,
                    reinitializePanel: options.reinitializePanel
                });
            }
        }
        // Otherwise simply send an event to notify the main controller that a feature has been added to the current
        // selection
        else if (options.sendEvent) {
            am.eventManager.trigger('feature_added_to_selection', {
                roofsideId: featureId,
                pointLocation: options.featureLocation
            });
        }

        return true;
    };

    /**
     * Unselects the roof side whose ID is passed as input argument
     * @fires SelectableProtobuf#feature_removed_from_selection
     * @fires SelectableProtobuf#feature_selection_cleared
     *
     * @param {String|Number} featureId - ID of the roof side to unselect
     * @param {Boolean} emitEvent - Should a 'feature_unselected' event be fired after the roof side is unselected? 
     *      (optional, defaults to true)
     */
    module.RoofLayerController.prototype._unselectFeature = function(featureId, emitEvent) {
        // Unselect feature on the map and remove its ID from the list of selected feature IDs
        this._vectorLayer.unselectFeature(featureId);
        this._selectedRoofsideIds.delete(featureId);

        // If required, emit event to notify that the feature was unselected
        emitEvent = (emitEvent == undefined) ? true : emitEvent;
        if (emitEvent) {
            am.eventManager.trigger('feature_removed_from_selection', {
                roofsideId: featureId,
                buildingId: this._selectedBuildingId,
            });
        }

        // If the feature to unselect was the only selected one, remove highlighting
        if (this._selectedRoofsideIds.size == 0) {
            this._vectorLayer.removeHighlighting();
            this._selectedBuildingId = undefined;
            if (emitEvent) {
                am.eventManager.trigger('feature_selection_cleared'); // emit an event to notify the listeners that all features have been unselected
            }
        }
    };

    /**
     * Hides the roof layer field to its original value. This function cancels the effect of
     * RoofLayerController#showRoofLayerField.
     */
    module.RoofLayerController.prototype.hideRoofLayerField = function() {
        if (this._controlButton) {
            $(this._controlButton.getContainer()).addClass('hidden');
        }
    };

    /**
     * Displays the roof layer field field to its original value. This function cancels the effect of
     * RoofLayerController#hideRoofLayerField.
     */
    module.RoofLayerController.prototype.showRoofLayerField = function() {
        if (this._controlButton) {
            $(this._controlButton.getContainer()).removeClass('hidden');
        }
    };

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Function returning a new instance of class am.RoofLayerController
     *
     * @param {Object} mainController - Reference to the main controller of the application
     * @param {Object} config - Configuration object to be passed to the constructor of class am.RoofLayerController
     * @return {am.RoofLayerController} - New instance of class am.RoofLayerController
     */
    module.roofLayerController = function(mainController, config) {
        var ctrl = new am.RoofLayerController();
        ctrl.init(mainController, config);
        return ctrl;
    };

    return module;

})(am.utils || {});